import * as XLSX from 'xlsx';

/**
 * Customer migration import template + parser (super admin).
 *
 * Column definitions for the customer import template. `header` is the
 * spreadsheet column name (matched case-insensitively but NOT renamed —
 * this is a template we define, so headers must match), `field` is the
 * API field name posted to pos-backend's customer-import endpoints.
 */
export const CUSTOMER_IMPORT_COLUMNS = [
  {
    header: 'phone',
    field: 'phone',
    required: true,
    description:
      "The customer's phone number — the unique identity key per merchant. Accepts 10-digit US (4085551234), 11-digit with leading 1, or +1 format; separators like dashes/spaces/parentheses are fine. If a customer with this phone already exists for the tenant, the row UPDATES that customer instead of creating a duplicate.",
  },
  {
    header: 'first_name',
    field: 'firstName',
    description: 'Customer first name. Recommended — the display name is built from first + last name.',
  },
  {
    header: 'last_name',
    field: 'lastName',
    description: 'Customer last name.',
  },
  {
    header: 'business_name',
    field: 'businessName',
    description:
      'Business name, mainly for credit/wholesale accounts. Used as the display name when first/last name are blank.',
  },
  {
    header: 'email',
    field: 'email',
    description: 'Customer email address (e.g. jane@example.com). Leave blank if unknown.',
  },
  {
    header: 'birthday',
    field: 'birthday',
    description: 'Customer birthday as YYYY-MM-DD (e.g. 1985-03-22). Leave blank if unknown.',
  },
  {
    header: 'customer_type',
    field: 'customerType',
    description:
      'One of: retail, credit, wholesale. Defaults to retail if blank. Must be credit or wholesale for the credit columns to be used.',
  },
  {
    header: 'credit_limit',
    field: 'creditLimit',
    description:
      "The customer's credit limit in dollars (e.g. 500 or 500.00). Only valid when customer_type is credit or wholesale. Leave blank for retail customers.",
  },
  {
    header: 'credit_balance_owed',
    field: 'creditBalanceOwed',
    description:
      'How much the customer currently OWES the store in dollars (credit used, not credit remaining). Becomes the opening balance in our system. Only valid when customer_type is credit or wholesale. May exceed credit_limit (imported with a warning).',
  },
  {
    header: 'reward_points',
    field: 'rewardPoints',
    description:
      'Reward/loyalty points the customer accumulated in the previous POS system, as a whole number (e.g. 1250). Becomes their starting points balance.',
  },
  {
    header: 'sms_opt_in',
    field: 'smsOptIn',
    description:
      'Enter Yes if the customer agreed to receive SMS. Accepts Yes/No/TRUE/FALSE/1/0. Blank = No for new customers; existing customers keep their current setting.',
  },
  {
    header: 'email_opt_in',
    field: 'emailOptIn',
    description: 'Enter Yes if the customer agreed to receive marketing email. Same accepted values as sms_opt_in.',
  },
  {
    header: 'legacy_customer_id',
    field: 'legacyCustomerId',
    description:
      "The customer's ID in the previous POS system. Stored for traceability so you can cross-reference after migration.",
  },
  { header: 'address_line1', field: 'addressLine1', description: 'Street address line 1.' },
  { header: 'address_line2', field: 'addressLine2', description: 'Street address line 2 (apt, suite...).' },
  { header: 'city', field: 'city', description: 'City.' },
  { header: 'state', field: 'state', description: 'State (e.g. CA).' },
  { header: 'postal_code', field: 'postalCode', description: 'ZIP / postal code.' },
  {
    header: 'notes',
    field: 'notes',
    description: 'Free-text notes shown on the customer profile.',
  },
] as const;

const HEADERS = CUSTOMER_IMPORT_COLUMNS.map((c) => c.header);

/** Row shape posted to pos-backend customer-import endpoints. */
export interface CustomerImportRow {
  phone: string;
  firstName?: string;
  lastName?: string;
  businessName?: string;
  email?: string;
  birthday?: string;
  customerType?: string;
  creditLimit?: number;
  creditBalanceOwed?: number;
  rewardPoints?: number;
  smsOptIn?: boolean;
  emailOptIn?: boolean;
  legacyCustomerId?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  notes?: string;
}

export interface CustomerParseResult {
  rows: CustomerImportRow[];
  skipped: number;
  errors: string[];
  warnings: string[];
}

/** Download a blank customer import template as XLSX (Customers + Instructions sheets). */
export function exportCustomerTemplate() {
  const wb = XLSX.utils.book_new();

  const exampleRows = [
    [
      '4085551234',
      'Jane',
      'Doe',
      '',
      'jane@example.com',
      '1985-03-22',
      'retail',
      '',
      '',
      '1250',
      'Yes',
      'Yes',
      'CUST-001',
      '123 Main St',
      '',
      'San Jose',
      'CA',
      '95112',
      '',
    ],
    [
      '4085555678',
      'Raj',
      'Patel',
      'Patel Wholesale LLC',
      '',
      '',
      'credit',
      '2000',
      '450.75',
      '80',
      'No',
      'No',
      'CUST-002',
      '',
      '',
      '',
      '',
      '',
      'Net-30 terms in old system',
    ],
  ];
  const ws = XLSX.utils.aoa_to_sheet([HEADERS, ...exampleRows]);
  ws['!cols'] = HEADERS.map((h) => ({ wch: Math.max(h.length + 4, 16) }));
  XLSX.utils.book_append_sheet(wb, ws, 'Customers');

  const instrData = [
    ['Column', 'Required', 'Description'],
    ...CUSTOMER_IMPORT_COLUMNS.map((c) => [c.header, 'required' in c && c.required ? 'Yes' : 'No', c.description]),
    [],
    ['IMPORTANT NOTES'],
    [
      '',
      '',
      'phone is the unique key per merchant: rows matching an existing customer UPDATE it, others create a new customer.',
    ],
    ['', '', 'credit_balance_owed is the amount the customer OWES (credit used) — not the credit they have remaining.'],
    ['', '', 'Blank cells never erase existing data when updating a customer; only filled cells overwrite.'],
    ['', '', 'Do not rename or reorder the column headers. The two sample rows can be deleted or overwritten.'],
    [
      '',
      '',
      'Re-importing the same file is safe: it converges to the same result (no double-counting of points or balances).',
    ],
  ];
  const instrWs = XLSX.utils.aoa_to_sheet(instrData);
  instrWs['!cols'] = [{ wch: 22 }, { wch: 10 }, { wch: 90 }];
  XLSX.utils.book_append_sheet(wb, instrWs, 'Instructions');

  XLSX.writeFile(wb, 'customer_import_template.xlsx');
}

function cellStr(v: unknown): string | undefined {
  if (v == null) return undefined;
  const s = String(v).trim();
  return s || undefined;
}

function cellNum(v: unknown, label: string, rowNum: number, errors: string[]): number | undefined {
  if (v == null || String(v).trim() === '') return undefined;
  const n = Number(String(v).replace(/[$,]/g, '').trim());
  if (!Number.isFinite(n)) {
    errors.push(`Row ${rowNum}: ${label} "${v}" is not a number`);
    return undefined;
  }
  return n;
}

function cellBool(v: unknown, label: string, rowNum: number, errors: string[]): boolean | undefined {
  if (v == null || String(v).trim() === '') return undefined;
  if (typeof v === 'boolean') return v;
  const s = String(v).trim().toLowerCase();
  if (['yes', 'true', '1', 'y'].includes(s)) return true;
  if (['no', 'false', '0', 'n'].includes(s)) return false;
  errors.push(`Row ${rowNum}: ${label} "${v}" must be Yes or No`);
  return undefined;
}

/** Excel may hand us a real Date or a string; normalize to YYYY-MM-DD. */
function cellDate(v: unknown, label: string, rowNum: number, errors: string[]): string | undefined {
  if (v == null || String(v).trim() === '') return undefined;
  if (v instanceof Date) {
    if (isNaN(v.getTime())) {
      errors.push(`Row ${rowNum}: ${label} is not a valid date`);
      return undefined;
    }
    return v.toISOString().slice(0, 10);
  }
  const s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (isNaN(d.getTime())) {
    errors.push(`Row ${rowNum}: ${label} "${s}" must be YYYY-MM-DD`);
    return undefined;
  }
  return d.toISOString().slice(0, 10);
}

/** Rough client-side phone sanity check; the server normalizes authoritatively. */
function phoneLooksValid(raw: string): boolean {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) return true;
  if (digits.length === 11 && digits.startsWith('1')) return true;
  return raw.trim().startsWith('+') && digits.length >= 10;
}

/**
 * Parse a filled customer import template (XLSX or CSV — XLSX.read handles
 * both). Strict on headers: every required column must be present under its
 * template name; unknown columns are ignored. Row numbers in errors are the
 * spreadsheet rows the user sees (header = row 1).
 */
export async function parseCustomersFromFile(file: File): Promise<CustomerParseResult> {
  const buffer = await file.arrayBuffer();
  const wb = XLSX.read(buffer);

  const rows: CustomerImportRow[] = [];
  const errors: string[] = [];
  const warnings: string[] = [];
  let skipped = 0;

  const sheetName = wb.SheetNames.find((n) => n.toLowerCase() !== 'instructions');
  if (!sheetName) {
    return { rows, skipped, errors: ['No data sheet found in file'], warnings };
  }

  const ws = wb.Sheets[sheetName];
  const grid: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1 });
  if (grid.length < 2) {
    return { rows, skipped, errors: ['File has no data rows'], warnings };
  }

  const headerRow = grid[0].map((h: any) =>
    String(h ?? '')
      .trim()
      .toLowerCase(),
  );
  const colIdx = new Map<string, number>();
  for (const col of CUSTOMER_IMPORT_COLUMNS) {
    const idx = headerRow.indexOf(col.header);
    if (idx === -1 && 'required' in col && col.required) {
      errors.push(`Missing required column "${col.header}" — download a fresh template and keep its headers.`);
    }
    if (idx !== -1) colIdx.set(col.field, idx);
  }
  if (errors.length > 0) return { rows, skipped, errors, warnings };

  const seenPhones = new Map<string, number>(); // digits -> first row using it

  for (let r = 1; r < grid.length; r++) {
    const raw = grid[r];
    const rowNum = r + 1; // spreadsheet row the user sees
    const get = (field: string): unknown => {
      const i = colIdx.get(field);
      return i === undefined || i >= raw.length ? undefined : raw[i];
    };

    // Fully empty row → silently skip (trailing rows in Excel exports).
    if (!raw || raw.every((c: unknown) => c == null || String(c).trim() === '')) {
      skipped++;
      continue;
    }

    const rowErrors: string[] = [];

    const phone = cellStr(get('phone'));
    if (!phone) {
      rowErrors.push(`Row ${rowNum}: phone is required`);
    } else if (!phoneLooksValid(phone)) {
      rowErrors.push(`Row ${rowNum}: phone "${phone}" doesn't look like a valid US phone`);
    } else {
      const digits = phone.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
      const firstUse = seenPhones.get(digits);
      if (firstUse !== undefined) {
        rowErrors.push(`Row ${rowNum}: duplicate phone "${phone}" (first used on row ${firstUse})`);
      } else {
        seenPhones.set(digits, rowNum);
      }
    }

    const email = cellStr(get('email'));
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      rowErrors.push(`Row ${rowNum}: email "${email}" is not a valid email address`);
    }

    const customerTypeRaw = cellStr(get('customerType'))?.toLowerCase();
    if (customerTypeRaw && !['retail', 'credit', 'wholesale'].includes(customerTypeRaw)) {
      rowErrors.push(`Row ${rowNum}: customer_type "${customerTypeRaw}" must be retail, credit, or wholesale`);
    }

    const creditLimit = cellNum(get('creditLimit'), 'credit_limit', rowNum, rowErrors);
    const creditBalanceOwed = cellNum(get('creditBalanceOwed'), 'credit_balance_owed', rowNum, rowErrors);
    const rewardPoints = cellNum(get('rewardPoints'), 'reward_points', rowNum, rowErrors);

    if (creditLimit !== undefined && creditLimit < 0) rowErrors.push(`Row ${rowNum}: credit_limit must be >= 0`);
    if (creditBalanceOwed !== undefined && creditBalanceOwed < 0)
      rowErrors.push(`Row ${rowNum}: credit_balance_owed must be >= 0`);
    if (rewardPoints !== undefined && (rewardPoints < 0 || !Number.isInteger(rewardPoints)))
      rowErrors.push(`Row ${rowNum}: reward_points must be a whole number >= 0`);

    const effectiveType = customerTypeRaw ?? 'retail';
    if (effectiveType === 'retail') {
      if ((creditLimit ?? 0) > 0)
        rowErrors.push(`Row ${rowNum}: credit_limit requires customer_type credit or wholesale`);
      if ((creditBalanceOwed ?? 0) > 0)
        rowErrors.push(`Row ${rowNum}: credit_balance_owed requires customer_type credit or wholesale`);
    } else if (creditLimit !== undefined && creditBalanceOwed !== undefined && creditBalanceOwed > creditLimit) {
      warnings.push(
        `Row ${rowNum}: balance owed (${creditBalanceOwed}) exceeds credit limit (${creditLimit}) — will import with the over-limit balance`,
      );
    }

    const smsOptIn = cellBool(get('smsOptIn'), 'sms_opt_in', rowNum, rowErrors);
    const emailOptIn = cellBool(get('emailOptIn'), 'email_opt_in', rowNum, rowErrors);
    const birthday = cellDate(get('birthday'), 'birthday', rowNum, rowErrors);

    if (rowErrors.length > 0) {
      errors.push(...rowErrors);
      skipped++;
      continue;
    }

    rows.push({
      phone: phone as string,
      firstName: cellStr(get('firstName')),
      lastName: cellStr(get('lastName')),
      businessName: cellStr(get('businessName')),
      email,
      birthday,
      customerType: customerTypeRaw,
      creditLimit,
      creditBalanceOwed,
      rewardPoints,
      smsOptIn,
      emailOptIn,
      legacyCustomerId: cellStr(get('legacyCustomerId')),
      addressLine1: cellStr(get('addressLine1')),
      addressLine2: cellStr(get('addressLine2')),
      city: cellStr(get('city')),
      state: cellStr(get('state')),
      postalCode: cellStr(get('postalCode')),
      notes: cellStr(get('notes')),
    });
  }

  return { rows, skipped, errors, warnings };
}
